import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';

import { listMembers, onMembersChanged, validateApiResponse } from '../../../services';
import { ADDED_FILTER_TO_API } from '../constants/membersFilters';
import { mapApiMemberToUi, UI_STATUS_TO_API } from '../utils/mapApiMemberToUi';

/** Service `filters` object from the list query: only what narrows, API vocabulary. */
const buildApiFilters = ({ status, role, group, mfa, added, importBatch }) => ({
  ...(status ? { status: UI_STATUS_TO_API[status] ?? status } : {}),
  ...(role?.length ? { role } : {}),
  ...(group?.length ? { group } : {}),
  ...(mfa ? { mfa } : {}),
  ...(added ? { added: ADDED_FILTER_TO_API[added] ?? added } : {}),
  ...(importBatch ? { import_batch_id: importBatch } : {}),
});

const hasNarrowing = ({ search, status, role, group, mfa, added, importBatch }) =>
  Boolean(search?.trim() || status || role?.length || group?.length || mfa || added || importBatch);

/**
 * The Members list: one page of rows for the committed query (search, filters, sort, page).
 * Refetches on every query change and when a member mutates elsewhere (without flashing the
 * skeleton). A late response of a superseded request is dropped.
 *
 * @param {object} query `{ search, status, role, group, mfa, sort: { field, direction }, page, pageSize }`
 * @param {object} [options]
 * @param {Function} [options.loadMembers] Injectable loader (defaults to the mock service).
 * @param {(lastPage: number) => void} [options.onPageOverflow] Called with the last page when the
 *   current page no longer exists (the list shrank), so the caller can move back instead of
 *   showing an empty page.
 */
export const useMembers = (query, { loadMembers = listMembers, onPageOverflow } = {}) => {
  const { t } = useTranslation('people-access');
  const [state, setState] = useState({ items: [], meta: null, isLoading: true, error: null });
  const fetchIdRef = useRef(0);
  const mountedRef = useRef(true);
  const queryRef = useRef(query);
  queryRef.current = query;
  const overflowRef = useRef(onPageOverflow);
  overflowRef.current = onPageOverflow;

  const { search, status, role, group, mfa, added, importBatch, sort, page, pageSize } = query;

  const fetchPage = useCallback(
    async ({ silent = false } = {}) => {
      const fetchId = ++fetchIdRef.current;
      const q = queryRef.current;
      if (!silent) setState((prev) => ({ ...prev, isLoading: true }));
      try {
        const res = await loadMembers({
          page: q.page,
          page_size: q.pageSize,
          order_by: q.sort.field,
          order_direction: q.sort.direction,
          filters: buildApiFilters(q),
          search: { query: q.search ?? '' },
        });
        const { body, meta } = validateApiResponse(res, t('members.list.loadError'));
        if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
        const totalPages = meta?.total_pages ?? 0;
        if (q.page > totalPages && totalPages >= 1 && overflowRef.current) {
          overflowRef.current(totalPages);
          return;
        }
        setState({
          items: (body?.items ?? []).map(mapApiMemberToUi),
          meta: meta ?? {},
          isLoading: false,
          error: null,
        });
      } catch (err) {
        if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: err?.message || t('members.list.loadError'),
        }));
      }
    },
    [loadMembers, t],
  );

  useEffect(() => {
    fetchPage();
  }, [fetchPage, search, status, role, group, mfa, added, importBatch, sort.field, sort.direction, page, pageSize]);

  useEffect(() => {
    mountedRef.current = true;
    const off = onMembersChanged(() => fetchPage({ silent: true }));
    return () => {
      mountedRef.current = false;
      off();
    };
  }, [fetchPage]);

  const refetch = useCallback(() => fetchPage(), [fetchPage]);

  return useMemo(() => {
    const { items, meta, isLoading, error } = state;
    const total = meta?.total ?? 0;
    const currentPage = meta?.page ?? page;
    const start = items.length ? (currentPage - 1) * pageSize + 1 : 0;
    const narrowing = hasNarrowing({ search, status, role, group, mfa, added, importBatch });
    const settled = !isLoading && !error && meta != null;

    return {
      rows: items,
      total,
      totalPages: meta?.total_pages ?? 1,
      page: currentPage,
      pageSize,
      rangeLabel: items.length ? `${start}-${start + items.length - 1}` : '0-0',
      hasPrevious: currentPage > 1,
      hasNext: currentPage < (meta?.total_pages ?? 1),
      isLoading,
      error,
      isEmptyCatalog: settled && total === 0 && !narrowing,
      isNoResults: settled && total === 0 && narrowing,
      refetch,
    };
  }, [state, page, pageSize, search, status, role, group, mfa, added, importBatch, refetch]);
};
